import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Card, Chip, Input } from '@/ui/primitives';
import { GridAuto, PageShell } from '@/ui/layout';
import { useSkills } from '@/modules/skills/api';
import { isOwnSkill } from '@/modules/skills/data';
import { SkillCard } from '@/modules/skills/components/SkillCard';
import { SkillDetailModal } from '@/modules/skills/components/SkillDetailModal';
import type { Skill } from '@/modules/skills/schemas';

export const Route = createFileRoute('/_authed/skills')({
  component: SkillsPage,
});

type Tab = 'proprias' | 'instaladas';

function SkillsPage() {
  const { data: skills = [], isLoading } = useSkills();
  const [tab, setTab] = useState<Tab>('proprias');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Skill | null>(null);

  const counts = useMemo(() => {
    let proprias = 0;
    let instaladas = 0;
    for (const s of skills) {
      if (isOwnSkill(s)) proprias++;
      else instaladas++;
    }
    return { proprias, instaladas };
  }, [skills]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return skills
      .filter((s) => (tab === 'proprias' ? isOwnSkill(s) : !isOwnSkill(s)))
      .filter((s) => {
        if (!q) return true;
        return (
          s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q)
        );
      });
  }, [skills, tab, query]);

  return (
    <PageShell
      eyebrow="Sistema"
      title="Skills"
      lead="Capacidades dos agentes — próprias da Ruralplan e instaladas de terceiros."
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-1.5">
            <TabButton
              active={tab === 'proprias'}
              count={counts.proprias}
              onClick={() => setTab('proprias')}
              label="Próprias"
            />
            <TabButton
              active={tab === 'instaladas'}
              count={counts.instaladas}
              onClick={() => setTab('instaladas')}
              label="Instaladas"
            />
          </div>

          <div className="relative w-full md:max-w-xs">
            <Search
              size={14}
              strokeWidth={1.5}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome ou descrição"
              className="pl-8"
            />
          </div>
        </div>

        {isLoading ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Carregando skills…</p>
          </Card>
        ) : filtered.length === 0 ? (
          <Card padding="lg">
            <p className="text-sm text-text-muted">Nenhuma skill encontrada.</p>
          </Card>
        ) : (
          <GridAuto min="md" gap="md">
            {filtered.map((s) => (
              <SkillCard key={s.name} skill={s} onSelect={() => setSelected(s)} />
            ))}
          </GridAuto>
        )}
      </div>

      <SkillDetailModal skill={selected} onClose={() => setSelected(null)} />
    </PageShell>
  );
}

interface TabButtonProps {
  active: boolean;
  count: number;
  label: string;
  onClick: () => void;
}

function TabButton({ active, count, label, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? 'inline-flex items-center gap-2 rounded-md bg-[var(--selection-soft)] px-3 py-1.5 text-sm font-medium text-text'
          : 'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-text-2 hover:bg-surface2 hover:text-text'
      }
    >
      {label}
      <Chip tone={active ? 'accent' : 'neutral'} size="sm">
        {count}
      </Chip>
    </button>
  );
}
